import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../chrome/AppShell';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { TCard, TEmpty, TRow, TSkeleton } from '../components/table';
import { Crumb, Dialog, Field, TextInput } from '../components/forms';
import useProduction from '@/src/Production/components/hooks/useProduction';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { PageHeadActions } from '../components/nav';
import {
    STAGE,
    STAGE_ORDER,
    dateRangeLabel,
    dayOf,
    humanize,
    timeRange,
} from '../lib/jobs';

// Jobs come from the Attio sync; renaming here is local only (a later sync may change it back).
// Provisional activations show only here until the deal is won.

function Frow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="frow">
            <span className="fl">{label}</span>
            <span className="fv">{children}</span>
        </div>
    );
}

function ago(iso: string) {
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return '';
    if (ms < 3600000) return `${Math.max(1, Math.round(ms / 60000))}m ago`;
    if (ms < 86400000) return `${Math.round(ms / 3600000)}h ago`;
    return `${Math.round(ms / 86400000)}d ago`;
}

export default function JobV3() {
    const { jobId } = useParams();
    const id = Number(jobId);
    const navigate = useNavigate();
    const api = useAuthenticatedApi();
    const [job, setJob] = useState<any>(null);
    const [state, setState] = useState<'loading' | 'ready' | 'failed' | 'missing'>('loading');
    const [adding, setAdding] = useState(false);
    const [copied, setCopied] = useState<'' | 'yes' | 'no'>('');
    const { updateJob } = useProduction();
    const { getOrgDomain } = useWhitelabelSettings();
    const { org } = useUserOrg();
    /* galleries live on the org's own domain (white-label), not this app's */
    const [galleryBase, setGalleryBase] = useState('');
    useEffect(() => {
        let alive = true;
        if (org?.id)
            getOrgDomain(org.id)
                .then((base) => alive && setGalleryBase(base ?? ''))
                .catch(() => {});
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);
    const [renaming, setRenaming] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [renameError, setRenameError] = useState('');

    const load = useCallback(() => {
        setState('loading');
        api.get(`/production/jobs/${id}`)
            .then((r: any) => {
                setJob(r.data);
                setState(r.data ? 'ready' : 'missing');
            })
            .catch((e: any) =>
                setState(e?.response?.status === 404 ? 'missing' : 'failed'),
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    useEffect(load, [load]);

    const addActivation = async () => {
        setAdding(true);
        try {
            const r: any = await api.post(`/production/jobs/${id}/activations`);
            if (r?.data?.id) navigate(`/activations/${r.data.id}`);
            else {
                setAdding(false);
                load();
            }
        } catch {
            setAdding(false);
        }
    };

    const rename = async () => {
        const name = draftName.trim();
        if (!name || savingName) return;
        setSavingName(true);
        setRenameError('');
        try {
            await updateJob(id, { name });
            setJob((j: any) => ({ ...j, name }));
            setRenaming(false);
        } catch {
            setRenameError('Couldn’t rename this job. Try again.');
        } finally {
            setSavingName(false);
        }
    };

    if (state !== 'ready') {
        return (
            <AppShell>
                <Crumb label="Pipeline" onClick={() => navigate('/pipeline')} />
                {state === 'loading' && (
                    <>
                        <h1 className="pg-title">&nbsp;</h1>
                        <p className="pg-sub">&nbsp;</p>
                        <TCard>
                            <TSkeleton rows={3} />
                        </TCard>
                    </>
                )}
                {state === 'failed' && (
                    <TCard>
                        <TEmpty message="Couldn’t load this job." actionLabel="Retry" onAction={load} />
                    </TCard>
                )}
                {state === 'missing' && (
                    <TCard>
                        <TEmpty message="This job doesn’t exist, or it was removed in Attio." actionLabel="Back to Pipeline" onAction={() => navigate('/pipeline')} />
                    </TCard>
                )}
            </AppShell>
        );
    }

    const acts: any[] = job.activations ?? [];
    const won = acts.filter((a) => !a.isProvisional);
    const provisional = acts.filter((a) => a.isProvisional);
    const first = acts.map((a) => a.startAt).filter(Boolean).sort()[0];
    const last = acts.map((a) => a.endAt ?? a.startAt).filter(Boolean).sort().slice(-1)[0];
    const s = dayOf(first);
    const e = dayOf(last);
    const days = s !== null && e !== null ? Math.round((e - s) / 86400000) + 1 : null;
    const crew = acts.reduce((n, a) => n + Number(a.staffCount ?? 0), 0);
    const stageKey = STAGE_ORDER.find((k) => acts.some((a) => a.status === k)) ?? 'pending';
    const stage = STAGE[stageKey] ?? { label: humanize(stageKey) };
    const galleryUrl = job.gallery?.slug
        ? `${galleryBase || window.location.origin}/gallery/${job.gallery.slug}`
        : '';

    const actRow = (a: any, i: number) => {
        const st = STAGE[a.status] ?? { label: humanize(a.status) };
        const to = `/activations/${a.activationId}`;
        return (
            <TRow
                key={a.activationId}
                index={i}
                name={a.name}
                badge={a.isProvisional ? 'Provisional' : undefined}
                meta={[a.venueName, a.staffCount ? `${a.staffCount} crew` : '', timeRange(a.startAt, a.endAt)]
                    .filter(Boolean)
                    .join(' · ')}
                metaDate={dateRangeLabel(a.startAt, a.endAt)}
                date={dateRangeLabel(a.startAt, a.endAt)}
                status={<span style={{ color: st.tone }}>{st.label}</span>}
                href={a.isProvisional ? undefined : to}
                onOpen={() => !a.isProvisional && navigate(to)}
                menuItems={
                    a.isProvisional
                        ? []
                        : [
                              { label: 'Open', onClick: () => navigate(to) },
                              { label: 'Sharing & Delivery', onClick: () => navigate(`${to}/sharing`) },
                          ]
                }
            />
        );
    };

    return (
        <AppShell>
            <Crumb label="Pipeline" onClick={() => navigate('/pipeline')} />
            <PageHeadActions
                title={job.name}
                sub={[humanize(job.eventType), job.billingEntity, dateRangeLabel(first, last)]
                    .filter(Boolean)
                    .join(' · ')}
                actions={
                    <>
                        {job.attioDealId && (
                            <a
                                className="tb-btn"
                                href={`https://app.attio.com/smilebooth/deals/record/${job.attioDealId}/overview`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: 'none' }}
                            >
                                Open in Attio
                            </a>
                        )}
                        <button
                            className="tb-btn"
                            onClick={() => {
                                setDraftName(job.name ?? '');
                                setRenameError('');
                                setRenaming(true);
                            }}
                        >
                            Rename
                        </button>
                        {/* copies the latest activation's production setup when there is one */}
                        <button className="tb-new" onClick={addActivation} disabled={adding}>
                            {adding ? 'Adding…' : 'Add activation'}
                        </button>
                    </>
                }
            />

            <div className="jobgrid">
                <aside className="stack">
                    <div className="sec">
                        <div className="sec-h">
                            <h2>Deal</h2>
                            <span className="n">from Attio</span>
                        </div>
                        <Frow label="Stage">
                            <span style={{ color: stage.tone }}>{stage.label}</span>
                        </Frow>
                        <Frow label="Client">{job.client?.name ?? job.billingEntity ?? '—'}</Frow>
                        <Frow label="Billing">{job.billingEntity ?? '—'}</Frow>
                        {job.boothType && <Frow label="Booth">{humanize(job.boothType)}</Frow>}
                        {job.owner && <Frow label="Owner">{job.owner}</Frow>}
                    </div>

                    <div className="sec">
                        <div className="sec-h">
                            <h2>Shared gallery</h2>
                        </div>
                        {job.gallery ? (
                            <>
                                <p style={{ margin: '0 0 2px' }}>{job.gallery.name}</p>
                                <p className="pg-sub" style={{ margin: '0 0 10px' }}>
                                    One gallery across all {won.length} activations
                                </p>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <a className="tb-btn" href={galleryUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                                        Open gallery
                                    </a>
                                    <button
                                        className="tb-btn"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(galleryUrl);
                                                setCopied('yes');
                                            } catch {
                                                setCopied('no');
                                            }
                                            window.setTimeout(() => setCopied(''), 1500);
                                        }}
                                    >
                                        {copied === 'yes' ? 'Copied' : copied === 'no' ? 'Couldn’t copy' : 'Copy link'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="pg-sub" style={{ margin: '0 0 10px' }}>
                                    No shared gallery — each activation keeps its own.
                                </p>
                                {/* creating a job gallery still lives on the classic job page */}
                                <a
                                    className="tb-btn"
                                    href={`/jobs/${id}`}
                                    onClick={(e) => {
                                        if (e.metaKey || e.ctrlKey) return;
                                        e.preventDefault();
                                        navigate(`/jobs/${id}`);
                                    }}
                                    style={{ textDecoration: 'none' }}
                                >
                                    Set up a shared gallery
                                </a>
                            </>
                        )}
                    </div>

                    <div className="sec">
                        <div className="sec-h">
                            <h2>Contacts</h2>
                            <span className="n">{(job.contacts ?? []).length}</span>
                        </div>
                        {(job.contacts ?? []).length === 0 ? (
                            <p className="pg-sub" style={{ margin: 0 }}>No contacts on this deal yet.</p>
                        ) : (
                            (job.contacts ?? []).map((c: any) => (
                                <div className="trow2" key={c.id}>
                                    <span className="nm2">
                                        {c.name}
                                        <span className="sub2">
                                            {[c.role, c.companyName].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                    <span className="right" style={{ display: 'flex', gap: 10 }}>
                                        {c.phone && (
                                            <a className="link" href={`tel:${c.phone}`}>
                                                Call
                                            </a>
                                        )}
                                        {c.email && (
                                            <a className="link" href={`mailto:${c.email}`}>
                                                Email
                                            </a>
                                        )}
                                        {c.attioId && (
                                            <a
                                                className="link"
                                                href={`https://app.attio.com/smilebooth/people/record/${c.attioId}/overview`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Attio
                                            </a>
                                        )}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </aside>

                <section>
                    <div className="tiles" style={{ maxWidth: 'none', marginBottom: 20 }}>
                        <div className="tile">
                            <p className="tl">Activations</p>
                            <div className="tn">{won.length}</div>
                            {provisional.length > 0 && (
                                <p className="tl" style={{ margin: '2px 0 0' }}>
                                    + {provisional.length} provisional
                                </p>
                            )}
                        </div>
                        <div className="tile">
                            <p className="tl">Days</p>
                            <div className="tn">{days ?? '—'}</div>
                        </div>
                        <div className="tile">
                            <p className="tl">Crew</p>
                            <div className="tn">{crew}</div>
                        </div>
                    </div>

                    <p className="s-lab">Activations</p>
                    {acts.length === 0 ? (
                        <TCard>
                            <TEmpty message="No activations on this job yet." />
                        </TCard>
                    ) : (
                        <TCard>
                            {won.map(actRow)}
                            {provisional.map((a, i) => actRow(a, won.length + i))}
                        </TCard>
                    )}
                    {provisional.length > 0 && (
                        <p className="tfoot">
                            Provisional activations appear only here until the deal is won.
                        </p>
                    )}

                    {/* the job endpoint doesn't send activity yet; shown when it does */}
                    {Array.isArray(job.activity) && job.activity.length > 0 && (
                        <>
                            <p className="s-lab" style={{ marginTop: 20 }}>Activity</p>
                            <TCard>
                                {job.activity.map((ev: any, i: number) => (
                                    <div className="trow" key={i} style={{ cursor: 'default' }}>
                                        <span className="main">
                                            <span className="nm">{ev.text}</span>
                                            <span className="sub">{ev.actor}</span>
                                        </span>
                                        <span className="dt">{ago(ev.at)}</span>
                                    </div>
                                ))}
                            </TCard>
                        </>
                    )}
                </section>
            </div>
            <Dialog
                open={renaming}
                title="Rename job"
                onClose={() => setRenaming(false)}
                onSubmit={rename}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setRenaming(false)}>Cancel</button>
                        <button type="submit" className="tb-new" disabled={!draftName.trim() || savingName}>
                            {savingName ? 'Saving…' : 'Rename'}
                        </button>
                    </>
                }
            >
                <Field label="Name" help="Attio stays the source of truth — a later sync may change the name back." error={renameError || undefined}>
                    <TextInput autoFocus value={draftName} onChange={(e) => setDraftName(e.target.value)} />
                </Field>
            </Dialog>
        </AppShell>
    );
}
